import { SnackbarProps } from '@mui/material';
import { authExchange } from '@urql/exchange-auth';
import { signOut } from 'firebase/auth';
import { createClient as createWSClient } from 'graphql-ws';
import { auth } from 'services/firebase';
import { config } from 'config/environments';
import { SnackbarVariant } from 'hooks/useSnackBar';
import { AuthEventContext, getAuthErrorDetails, recordAuthEvent } from 'utils/auth-instrumentation';
import { isPermanentAuthError } from 'utils/firebase-error-checks';
import { isHeadless } from 'utils/headless';
import refreshFirebaseUser from 'utils/refresh-firebase-user';
import { getPreviousRoute, setPreviousRoute } from '../utils/routeHistory.utils';
import { clearStore } from './idb-store';
import { clearOutbox } from './outbox';
import { getAdminProfileFromVariant, getPrefixedRoute, NILE_ADMIN_PROFILE_ID } from './theming';

const getTokenExpiryTime = (token: string) => {
  // Split the token into parts
  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }

  // Decode the payload (second part)
  const payload = JSON.parse(atob(parts[1]));

  // Get the expiry time
  const expiryTime = payload.exp;

  // Convert to milliseconds and return as a Date object
  return expiryTime ? new Date(expiryTime * 1000) : null;
};

const getToken = async (forceRefresh = false) => {
  if (!navigator.onLine && !forceRefresh && auth.currentUser) {
    try {
      const cachedToken = await auth.currentUser.getIdToken(false);
      return cachedToken || null;
    } catch {
      return null; // Return null if cached token retrieval fails
    }
  }

  return resetToken(forceRefresh);
};

/** The headers the GraphQL client authenticates with, for plain HTTP requests to gapi. */
export const getAuthHeaders = async () => {
  const token = await getToken();
  const headers: Record<string, string> = {
    'X-Platform': getAdminProfileFromVariant() || NILE_ADMIN_PROFILE_ID.toString(),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const wssURL = config.gapiURL.replace('http', 'ws');

export const createWebSocketClient = () => {
  const platformValue = getAdminProfileFromVariant();
  return createWSClient({
    url: wssURL,
    connectionParams: async () => {
      const token = await getToken();
      const params: Record<string, string> = {
        Authorization: `Bearer ${token}`,
        'X-Platform': platformValue || NILE_ADMIN_PROFILE_ID.toString(),
      };
      return params;
    },
    keepAlive: 30000, // 30 seconds to prevent 5-minute timeout on Safari
    retryAttempts: Infinity, // Automatic reconnection if needed
    retryWait: async () => await new Promise((resolve) => setTimeout(resolve, 5000)), // 5-second retry delay
  });
};

export const resetToken = async (forceRefresh = false) => {
  const firebaseUser = auth.currentUser;
  if (!firebaseUser) return null;
  const token = await refreshFirebaseUser({
    user: firebaseUser,
    forceRefresh: forceRefresh,
  });
  return token;
};

export const resetStorage = async () => {
  // Preserve previous route before clearing
  const prevRoute = getPreviousRoute();

  localStorage.clear();
  await clearStore();
  // Queued changes belong to this session; never replay them for whoever signs in next.
  await clearOutbox();
  sessionStorage.clear();

  // Restore previous route after clearing
  if (prevRoute) {
    setPreviousRoute(prevRoute);
  }
};

export const logout = async (
  withRedirect = true,
  showSnackbar?: (props: SnackbarProps, variant?: SnackbarVariant) => void,
  logoutContext?: AuthEventContext,
) => {
  recordAuthEvent('logout', {
    source: logoutContext?.source ?? 'auth.logout',
    reason: logoutContext?.reason ?? 'explicit logout',
    withRedirect,
    operationKind: logoutContext?.operationKind,
    operationKey: logoutContext?.operationKey,
    errorMessage: logoutContext?.errorMessage,
    errorCode: logoutContext?.errorCode,
  });

  try {
    await resetToken(true);
    if (auth.currentUser) await signOut(auth);
    await resetStorage();
    if (withRedirect) {
      window.location.href = getPrefixedRoute('login');
    }
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    error.name = 'SignOutError';
    if (showSnackbar)
      showSnackbar(
        {
          message:
            'An error occurred while logging you out. Please try again. If the issue persists, feel free to contact our team for assistance.',
          autoHideDuration: 3000,
          color: 'error',
        },
        SnackbarVariant.ERROR,
      );
  }
};

const createAuthExchange = (showSnackbar?: (props: SnackbarProps, variant?: SnackbarVariant) => void) => {
  let token: string | null = null; // Track the token

  return authExchange(async (utils) => {
    token = await getToken(); // Fetch the token once when creating the exchange

    return {
      addAuthToOperation(operation) {
        // Skip adding auth headers when offline
        if (!navigator.onLine) {
          return operation; // Return operation without auth headers
        }
        const platformValue = getAdminProfileFromVariant();
        const prevRoute = getPreviousRoute();
        let headers: Record<string, string> = {
          'X-Platform': platformValue || NILE_ADMIN_PROFILE_ID.toString(),
          'X-Previous-Route': prevRoute,
        };

        // Add authorization if token exists
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
        return utils.appendHeaders(operation, headers);
      },
      willAuthError() {
        // Skip auth error check when offline
        if (!navigator.onLine) {
          return false; // No auth error for offline operations
        }

        let tokenHasExpired = false;
        if (token) {
          const expiryTime = getTokenExpiryTime(token);
          tokenHasExpired = !expiryTime || expiryTime < new Date();
        }

        return !token || tokenHasExpired;
      },
      didAuthError(error, operation) {
        // Skip error handling when offline
        if (!navigator.onLine) {
          return false; // Prevent logout or error handling when offline
        }

        const isForbidden = error.graphQLErrors.some((e) => e.extensions?.code === 'FORBIDDEN');
        if (isForbidden) {
          // Harvester authenticates a headless page that has no user of its own, so there is no
          // session to end, and a redirect to login would replace the page it is printing.
          if (isHeadless() && !auth.currentUser) {
            return false;
          }
          void logout(true, showSnackbar, {
            source: 'authExchange.didAuthError',
            reason: 'backend returned FORBIDDEN',
            operationKind: operation.kind,
            operationKey: operation.key,
          });
          return false;
        }

        const isErrorMessage = error.graphQLErrors.some(
          (e) => e.extensions?.code && ['UNAUTHORIZED', 'ERROR_MESSAGE'].includes(e.extensions.code.toString()),
        );
        if (isErrorMessage) {
          // Let getErrorSeverity classify the message (orange for expected,
          // user-fixable errors; red only for "something went wrong"). Forcing
          // color here would paint every whitelisted message red.
          showSnackbar &&
            showSnackbar(
              {
                message: error.graphQLErrors[0].message,
                autoHideDuration: 3000,
              },
              SnackbarVariant.ERROR,
            );
          return false;
        }

        return error.graphQLErrors.some((e) => e.extensions?.code === 'REFRESH_TOKEN');
      },
      async refreshAuth() {
        // Skip token refresh when offline
        if (!navigator.onLine) {
          return; // Avoid attempting to refresh token offline
        }

        try {
          token = await getToken(true); // Update the token
        } catch (error) {
          const errorDetails = getAuthErrorDetails(error);

          if (!isPermanentAuthError(error)) {
            recordAuthEvent('refresh-auth-failed-transient', {
              source: 'authExchange.refreshAuth',
              reason: 'token refresh failed with transient error',
              ...errorDetails,
            });
            return;
          }

          recordAuthEvent('refresh-auth-failed-permanent', {
            source: 'authExchange.refreshAuth',
            reason: 'token refresh failed with permanent auth error',
            ...errorDetails,
          });
          await logout(true, showSnackbar, {
            source: 'authExchange.refreshAuth',
            reason: 'token refresh failed with permanent auth error',
            ...errorDetails,
          });
        }
      },
    };
  });
};

export default createAuthExchange;
